import './ProjectThumbItem.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faDrawPolygon, faEllipsisV, faInfoCircle, faPen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { Button, Dropdown, IconButton, ListDivider, Menu, MenuButton, MenuItem } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { useHistory } from 'react-router';
import { S_Link2 } from '../../../../../design/links/S_Link2';
import { TrashIcon } from '../../../../../design/static/icons/TrashIcon';
import { DropdownLinkItem } from '../../../../components/DropdownLinkItem';
import { EnableFor } from '../../../../containers/EnableForRole';
import { ProjectRole } from '../../../../models/userRole.model';
import { Home } from '../../../../routes/config/Home';
import { PublishButton } from '../../../projectDetails/components/PublishButton';
import { PublishButtonContainer } from '../../../projectDetails/containers/PublishButton.container';
import { useProjectPermissions } from '../../projects.permissions';

library.add(faInfoCircle, faPen, faDrawPolygon);

interface IProps {
  id: string;
  name: string;
  description: string;
  publishReady: boolean;
  workspaceId: string;
  deleteDraft(draftId: string): Promise<void>;
  projectRole: ProjectRole;
}

export const DraftProjectThumbItem = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  const history = useHistory();
  const projectsPermissions = useProjectPermissions();

  return (
    <div className='project-thumb'>
      <div className='project-thumb-head'>
        <div className='name'>
          <S_Link2
            color='text'
            to={Home.Projects.Details.AnnotationView.withParams({ projectId: props.id, workspaceId: props.workspaceId })}
          >
            {props.name}
          </S_Link2>
        </div>
        <PublishButtonContainer
          projectId={props.id}
          disabled={!props.publishReady}
        >
          {(publishProps): ReactElement => (
            <Dropdown>
              <MenuButton
                slots={{ root: IconButton }}
                slotProps={{ root: { variant: 'plain', color: 'neutral' } }}
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </MenuButton>
              <Menu placement='bottom-end'>
                <EnableFor
                  hasPermission={projectsPermissions.canView()}
                  hideWhenNoAccess
                >
                  <DropdownLinkItem linkTo={Home.Projects.Details.AnnotationView.withParams({ projectId: props.id, workspaceId: props.workspaceId })}>
                    {projectsPermissions.canEdit().allowed ? t('edit') : t('project_details')}
                  </DropdownLinkItem>
                </EnableFor>
                <EnableFor
                  hasPermission={projectsPermissions.canPublish()}
                  hideWhenNoAccess
                >
                  <MenuItem
                    onClick={() => {
                      void publishProps.onClick();
                    }}
                    disabled={publishProps.disabled}
                  >
                    {t('publish')}
                  </MenuItem>
                </EnableFor>
                <EnableFor
                  hasPermission={projectsPermissions.canDelete()}
                  hideWhenNoAccess
                >
                  <ListDivider />
                  <MenuItem
                    onClick={(): void => {
                      void props.deleteDraft(props.id);
                    }}
                  >
                    <TrashIcon /> {t('delete_project')}
                  </MenuItem>
                </EnableFor>
              </Menu>
            </Dropdown>
          )}
        </PublishButtonContainer>
      </div>
      {props.description && (
        <div className='info'>
          <div className='description'>{props.description}</div>
        </div>
      )}
      <div className='alerts'>
        {props.publishReady ? (
          <div className='alerts-info'>
            <FontAwesomeIcon
              icon={faInfoCircle}
              className='alert-icon'
            />
            {t('this_project_has_not_been_published')}
          </div>
        ) : (
          <div className='alerts-info'>
            <FontAwesomeIcon
              icon={faInfoCircle}
              className='alert-icon'
            />
            {t('draft_configuration_is_not_finished')}
          </div>
        )}
      </div>
      <div className='bottom'>
        <EnableFor hasPermission={projectsPermissions.canEdit()}>
          <Button
            variant='outlined'
            onClick={() => {
              history.push(Home.Projects.Details.AnnotationView.withParams({ projectId: props.id, workspaceId: props.workspaceId }));
            }}
          >
            {t('edit')}
          </Button>
          <PublishButtonContainer
            projectId={props.id}
            disabled={!props.publishReady}
          >
            {(childrenProps): ReactElement => <PublishButton {...childrenProps} />}
          </PublishButtonContainer>
        </EnableFor>
      </div>
    </div>
  );
});
